import { useEffect, useState } from "react";
import { Coins, Crown } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MEDAL = ["bg-gradient-to-br from-yellow-200 to-amber-500 text-amber-950", "bg-gradient-to-br from-slate-100 to-slate-400 text-slate-800", "bg-gradient-to-br from-orange-200 to-orange-500 text-orange-950"];

function Row({ r, me }) {
  return (
    <li className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 ${me ? "bg-amber-300/20 ring-1 ring-amber-300/60" : "bg-white/5"}`} data-testid={`goldrain-record-${r.place}`}>
      <span className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-black ${MEDAL[r.place - 1] || "bg-white/10 text-white"}`}>{r.place === 1 ? <Crown className="w-4 h-4" /> : r.place}</span>
      {r.picture ? <img src={imgSrc(r.picture)} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-amber-300/70" />
        : <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center font-black text-amber-800">{r.name?.[0]?.toUpperCase()}</div>}
      <div className="min-w-0 flex-1">
        <p className="font-bold text-white truncate">{r.name}{me && " (du)"}</p>
        <p className="text-[11px] text-amber-100/70">{r.rains}× beim Goldregen dabei</p>
      </div>
      <span className="font-display font-black text-amber-300 tabular-nums flex items-center gap-1">{r.coins.toLocaleString("de-DE")} <Coins className="w-4 h-4" /></span>
    </li>
  );
}

export function GoldRainRecords() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/public/goldrain-records").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  useEffect(() => { if (user) api.get("/goldrain/records/me").then(({ data }) => setMe(data)).catch(() => {}); }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!list) return null;
  const inTop = me && list.some((r) => r.user_id === user?.user_id);
  return (
    <section className="mt-10 relative overflow-hidden rounded-[2rem] bg-gradient-to-b from-rose-900 via-rose-950 to-black border-2 border-amber-300/50 p-5 sm:p-7 shadow-xl shadow-amber-200/30" data-testid="goldrain-records">
      <div className="gold-banner absolute inset-x-0 top-0 h-1.5" aria-hidden />
      <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-amber-300 flex items-center gap-1.5"><Coins className="w-3.5 h-3.5" /> Ewige Bestenliste</p>
      <h2 className="mt-1 font-display text-2xl sm:text-3xl font-black text-white">Goldregen-Rekorde</h2>
      <p className="mt-1 text-sm text-amber-100/70">Die meisten gefangenen Münzen aus allen Goldregen zusammen.</p>
      {list.length === 0 ? <p className="mt-5 text-sm text-white/60" data-testid="goldrain-records-empty">Noch keine Münzen gefangen – beim nächsten Goldregen kannst du der Erste sein!</p> : (
        <ol className="mt-5 space-y-2" data-testid="goldrain-records-list">{list.map((r) => <Row key={r.user_id} r={r} me={r.user_id === user?.user_id} />)}</ol>
      )}
      {me && !inTop && (
        <p className="mt-4 rounded-2xl bg-white/10 px-4 py-3 text-sm text-white" data-testid="goldrain-records-me">
          {me.place ? <>Dein Platz: <b className="text-amber-300">{me.place}</b> mit <b className="text-amber-300">{me.coins}</b> Münzen</> : "Du hast noch keine Goldregen-Münze gefangen – beim nächsten Mal bist du dabei!"}
        </p>
      )}
    </section>
  );
}
